/**
 * SandboxLauncher — Launch, stop, and destroy the org's sandbox container.
 *
 * Shows current lifecycle state with color-coded badge and appropriate action buttons.
 */
import { useEffect } from "react";
import {
  Play,
  Square,
  Trash2,
  Server,
  RefreshCw,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { useSandboxStore } from "@/stores/useSandboxStore";
import type { SandboxLifecycleState } from "@ajnt/shared";

/* ------------------------------------------------------------------ */
/*  State → visual mapping                                             */
/* ------------------------------------------------------------------ */

const stateConfig: Record<
  SandboxLifecycleState,
  { label: string; color: string; badgeVariant: "success" | "warning" | "danger" | "default" | "primary"; pulse?: boolean }
> = {
  creating: { label: "Creating", color: "text-amber-400", badgeVariant: "warning", pulse: true },
  provisioning: { label: "Provisioning", color: "text-amber-400", badgeVariant: "warning", pulse: true },
  active: { label: "Ready", color: "text-emerald-400", badgeVariant: "success", pulse: true },
  sleeping: { label: "Sleeping", color: "text-gray-400", badgeVariant: "default" },
  waking: { label: "Waking", color: "text-amber-400", badgeVariant: "warning", pulse: true },
  error: { label: "Error", color: "text-red-400", badgeVariant: "danger" },
  terminated: { label: "Terminated", color: "text-gray-500", badgeVariant: "default" },
};

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function SandboxLauncher() {
  const {
    sandbox,
    loading,
    error,
    fetchSandboxes,
    fetchSandboxStatus,
    launchSandbox,
    wakeSandbox,
    stopSandbox,
    destroySandbox,
    clearError,
  } = useSandboxStore();

  useEffect(() => {
    fetchSandboxes();
  }, [fetchSandboxes]);

  // Poll status when sandbox exists and is in a transitional state
  useEffect(() => {
    if (!sandbox?.id) return;
    const transitional = ["creating", "provisioning", "waking"];
    if (!transitional.includes(sandbox.state)) return;

    const interval = setInterval(() => {
      fetchSandboxStatus(sandbox.id);
    }, 5000);

    return () => clearInterval(interval);
  }, [sandbox?.id, sandbox?.state, fetchSandboxStatus]);

  const state = sandbox?.state ?? "terminated";
  const config = stateConfig[state] ?? stateConfig.terminated;

  const canLaunch = !sandbox || state === "terminated";
  const canWake = state === "sleeping";
  const canStop = state === "active";
  const canDestroy =
    state === "active" || state === "sleeping" || state === "error";
  const isTransitional = ["creating", "provisioning", "waking"].includes(state);

  return (
    <Card data-testid="sandbox-launcher">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Server className="h-5 w-5 text-primary" />
          Sandbox Container
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-danger/30 bg-red-950/40 px-4 py-3 text-sm text-red-200">
            <AlertTriangle className="h-4 w-4 shrink-0 text-red-400" />
            <span className="flex-1">{error}</span>
            <button
              onClick={clearError}
              className="text-xs text-red-400 hover:text-red-200 underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Status section */}
        {sandbox ? (
          <div className="rounded-lg border border-white/5 bg-surface px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Pulsing status dot */}
                <span className="relative flex h-3 w-3">
                  {config.pulse && (
                    <span
                      className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${
                        state === "active"
                          ? "bg-emerald-400"
                          : state === "error"
                            ? "bg-red-400"
                            : "bg-amber-400"
                      }`}
                    />
                  )}
                  <span
                    className={`relative inline-flex h-3 w-3 rounded-full ${
                      state === "active"
                        ? "bg-emerald-500"
                        : state === "error"
                          ? "bg-red-500"
                          : state === "sleeping" || state === "terminated"
                            ? "bg-gray-500"
                            : "bg-amber-500"
                    }`}
                  />
                </span>
                <Badge variant={config.badgeVariant}>{config.label}</Badge>
              </div>

              {isTransitional && <Spinner />}
            </div>

            {/* Details */}
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              {sandbox.instanceType && (
                <div>
                  <p className="text-muted-foreground">Instance</p>
                  <p className="font-medium text-foreground">
                    {sandbox.instanceType}
                  </p>
                </div>
              )}
              {sandbox.containerId && (
                <div>
                  <p className="text-muted-foreground">Container ID</p>
                  <p className="font-mono text-xs text-foreground truncate">
                    {sandbox.containerId}
                  </p>
                </div>
              )}
              {sandbox.uptimeSeconds != null && sandbox.uptimeSeconds > 0 && (
                <div>
                  <p className="flex items-center gap-1 text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    Uptime
                  </p>
                  <p className="font-medium text-foreground">
                    {formatUptime(sandbox.uptimeSeconds)}
                  </p>
                </div>
              )}
              {sandbox.lastActiveAt && (
                <div>
                  <p className="text-muted-foreground">Last Active</p>
                  <p className="text-foreground">
                    {formatDate(sandbox.lastActiveAt)}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-white/5 bg-surface px-5 py-8 text-center">
            <Server className="mx-auto h-10 w-10 text-muted-foreground/50" />
            <p className="mt-2 text-sm text-muted-foreground">
              No sandbox provisioned yet
            </p>
            <p className="text-xs text-muted-foreground/70">
              Launch a sandbox to start coding with AI
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3">
          {canLaunch && (
            <Button
              onClick={() => launchSandbox()}
              loading={loading}
              size="md"
            >
              <Play className="h-4 w-4" />
              Launch Sandbox
            </Button>
          )}

          {canWake && (
            <Button
              onClick={() => wakeSandbox(sandbox!.id)}
              loading={loading}
              size="md"
            >
              <RefreshCw className="h-4 w-4" />
              Wake Sandbox
            </Button>
          )}

          {canStop && (
            <Button
              variant="secondary"
              onClick={() => stopSandbox(sandbox!.id)}
              loading={loading}
              size="md"
            >
              <Square className="h-4 w-4" />
              Stop Sandbox
            </Button>
          )}

          {canDestroy && (
            <Button
              variant="danger"
              onClick={() => {
                if (window.confirm("Are you sure you want to destroy this sandbox? This cannot be undone.")) {
                  destroySandbox(sandbox!.id);
                }
              }}
              loading={loading}
              size="md"
            >
              <Trash2 className="h-4 w-4" />
              Destroy
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
